import { Field } from "@components/ui/Field"
import { FieldRow } from "@components/ui/FieldRow"
import { Input } from "@components/ui/Input"
import { Select } from "@components/ui/Select"
import { useRPCQuery } from "eddev/hooks"
import { MutableRefObject, useState } from "react"
import { AccountDetails, MembershipState } from "../../../server/routes/api.memberships"
import { AreaSpinner } from "@components/ui/Spinner"
import { Alert } from "@components/ui/Alert"

type Props = {
  account: AccountDetails
  membership?: MembershipState
  disabled?: boolean
  formRef: MutableRefObject<MyInfoForm>
  hideEmailField?: boolean
}

export type MyInfoForm = {
  confirm(): AccountDetails
}

export function AccountDetailsForm(props: Props) {
  const schemaLookup = useRPCQuery("api.memberships.accountSchema", undefined)

  const [values, setValues] = useState<AccountDetails>(props.account)

  const setter = (key: string) => (value: any) => {
    setValues((prev) => ({ ...prev, [key]: value }))
  }

  const bind = (key: keyof AccountDetails) => ({
    value: values[key] ?? "",
    onValueChange: setter(key),
    disabled: props.disabled,
  })

  props.formRef.current = {
    confirm() {
      return values
    },
  }

  if (schemaLookup.isPending) {
    return <AreaSpinner />
  } else if (schemaLookup.isError) {
    return <Alert error>{schemaLookup.error.message}</Alert>
  }

  return (
    <div className="stack-y-5">
      <FieldRow>
        <Field label="First Name" required>
          <Input type="text" stretch required className="w-full" placeholder="" {...bind("firstName")} />
        </Field>
        <Field label="Last Name" required>
          <Input type="text" stretch required className="w-full" {...bind("lastName")} placeholder="" />
        </Field>
      </FieldRow>
      {!props.hideEmailField && (
        <Field label="Email" required>
          <Input type="email" stretch required className="w-full" placeholder="" {...bind("email")} />
        </Field>
      )}
      <FieldRow>
        <Field label="Phone" required>
          <Input type="tel" stretch required className="w-full" placeholder="" {...bind("phone")} />
        </Field>
        <Field label="Secondary Phone">
          <Input type="tel" stretch className="w-full" placeholder="" {...bind("secondaryPhone")} />
        </Field>
      </FieldRow>
      <Field label="Address" required>
        <Input type="text" stretch required className="w-full" {...bind("address")} placeholder="" />
      </Field>
      <FieldRow>
        <Field label="City" required>
          <Input
            type="text"
            stretch
            required
            className="w-full"
            innerClassName="w-full"
            {...bind("city")}
            placeholder=""
            {...bind("city")}
          />
        </Field>
        <Field label="State" required>
          <Select
            {...bind("state")}
            stretch
            required
            className="w-full"
            triggerLabelClassName="text-ellipsis overflow-hidden pt-1 w-full mt-0"
            items={US_STATES}
          />
        </Field>
        <Field label="Zip Code" required>
          <Input
            type="text"
            stretch
            required
            className="w-full"
            innerClassName="w-full"
            {...bind("zip")}
            placeholder=""
            {...bind("zip")}
          />
        </Field>
      </FieldRow>
      {props.membership?.hasPremiumOne && (
        <div>
          <div className="type-label-l mb-1">Your Plus One</div>
          <p className="type-body-xs mb-3">Your membership entitles you to a plus one. Enter their details below:</p>
          <FieldRow>
            <Field label="First Name" required>
              <Input type="text" required stretch className="w-full" placeholder="" {...bind("plusFirstName")} />
            </Field>
            <Field label="Last Name" required>
              <Input type="text" required stretch className="w-full" {...bind("plusLastName")} placeholder="" />
            </Field>
          </FieldRow>
        </div>
      )}
    </div>
  )
}

const US_STATES = Object.entries({
  Alabama: "AL",
  Alaska: "AK",
  Arizona: "AZ",
  Arkansas: "AR",
  California: "CA",
  Colorado: "CO",
  Connecticut: "CT",
  Delaware: "DE",
  "District of Columbia": "DC",
  Florida: "FL",
  Georgia: "GA",
  Hawaii: "HI",
  Idaho: "ID",
  Illinois: "IL",
  Indiana: "IN",
  Iowa: "IA",
  Kansas: "KS",
  Kentucky: "KY",
  Louisiana: "LA",
  Maine: "ME",
  Montana: "MT",
  Nebraska: "NE",
  Nevada: "NV",
  "New Hampshire": "NH",
  "New Jersey": "NJ",
  "New Mexico": "NM",
  "New York": "NY",
  "North Carolina": "NC",
  "North Dakota": "ND",
  Ohio: "OH",
  Oklahoma: "OK",
  Oregon: "OR",
  Maryland: "MD",
  Massachusetts: "MA",
  Michigan: "MI",
  Minnesota: "MN",
  Mississippi: "MS",
  Missouri: "MO",
  Pennsylvania: "PA",
  "Rhode Island": "RI",
  "South Carolina": "SC",
  "South Dakota": "SD",
  Tennessee: "TN",
  Texas: "TX",
  Utah: "UT",
  Vermont: "VT",
  Virginia: "VA",
  Washington: "WA",
  "West Virginia": "WV",
  Wisconsin: "WI",
  Wyoming: "WY",
}).map(([label, value]) => ({ value: value, label: label }))
